import { gsap, SplitText } from "../LibrariesImport";
import { GlobalVariables } from "../GlobalVariables";

// Révélation dédiée de tout titre "fort" du site (.title-banner, .title-level-1,
// .title-level-2) — global et automatique, aucun attribut à poser dans les
// twigs. Quand un titre est précédé de son surtitre (.tag > .tag-dot +
// .tag-label), les deux sont orchestrés ensemble : fondu du contour du tag,
// pastille qui rebondit, wipe horizontal du label, puis focus-in (flou +
// montée) mot par mot sur le titre. Sans surtitre, seul le titre joue son
// focus-in. Ni `data-anim` (pas de clip-path/filter) ni `DataTextAppear`
// (reveal ligne uniforme) ne couvrent cette hiérarchie surtitre -> titre en un
// seul geste, d'où un composant dédié.
//
// Le fondu porte sur le .tag lui-même (et non sur ses seuls enfants) : sans lui,
// la pastille bordée — bordure + encoche ::before — restait affichée pendant que
// dot et label étaient encore masqués, donnant un tag vide qui flotte au scroll.
// Uniquement autoAlpha, jamais de transform : .tag:has(.tag-dot) porte déjà un
// translateX(0.35rem) en CSS pour caler son encoche, que GSAP écraserait.
//
// Un court délai (REVEAL_DELAY) précède le jeu : sans lui, sur les bannières
// above-the-fold, le ScrollTrigger "top 85%" est déjà satisfait au chargement
// et l'anim se joue avant même que l'œil ait fini de se poser sur la page.
//
// La bannière home (3D, scroll-scrub) est hors périmètre — son titre est déjà
// piloté par HomeBannerTextReveal.
const TITLE_SELECTOR = ".title-banner, .title-level-1, .title-level-2";
const REVEAL_DELAY = 0.2;

export default class TitleReveal {
	constructor(root = document) {
		const titles = new Set(root.querySelectorAll(TITLE_SELECTOR));

		// Titres des panneaux de sinistre (solution-disasters.twig) : masqués au chargement, ils
		// seraient découpés sur une largeur nulle puis redécoupés (et réanimés) à leur affichage,
		// d'où un saut du texte à chaque changement de sinistre. Affichés sans révélation.
		titles.forEach((title) => {
			if (title.closest("[data-solution-disaster-panel]")) {
				titles.delete(title);
				gsap.set(title, { opacity: 1 });
			}
		});

		root.querySelectorAll(".tag-dot").forEach((dot) => {
			const tag = dot.closest(".tag");
			const title = tag?.parentElement.querySelector(TITLE_SELECTOR);
			if (title && titles.has(title)) {
				titles.delete(title);
				this.animate(title, tag);
			}
		});

		titles.forEach((title) => this.animate(title, null));

		// Tags sans titre derrière eux (footer, cartes, blocs…) : ils jouent seuls leur apparition
		root.querySelectorAll(".tag-dot").forEach((dot) => {
			const tag = dot.closest(".tag");
			if (tag && !tag.dataset.titleReveal && !tag.closest(".header-main, [data-anim]")) {
				this.animateTag(tag);
			}
		});
	}

	animateTag(tag) {
		tag.dataset.titleReveal = "true";

		if (GlobalVariables.reducedMotion) {
			return;
		}

		const dot = tag.querySelector(".tag-dot");
		const label = tag.querySelector(".tag-label");

		gsap.set(tag, { autoAlpha: 0 });
		gsap.set(dot, { scale: 0, transformOrigin: "50% 50%" });
		gsap.set(label, { clipPath: "inset(0 100% 0 0)" });

		// Positions absolues plutôt que des décalages relatifs : le fondu du contour
		// et le rebond de la pastille partent ensemble, sinon on voit passer un
		// cadre vide avant son contenu.
		return gsap.timeline({ scrollTrigger: { trigger: tag, start: "top 85%" }, delay: REVEAL_DELAY })
			.to(tag, { autoAlpha: 1, duration: 0.15, ease: "power1.out" }, 0)
			.to(dot, { scale: 1, duration: 0.15, ease: "back.out(3)" }, 0)
			.to(label, { clipPath: "inset(0 0% 0 0)", duration: 0.3, ease: "power2.inOut" }, 0.1);
	}

	animate(title, tag) {
		if (GlobalVariables.reducedMotion) {
			gsap.set(title, { opacity: 1 });
			return;
		}

		if (tag) {
			this.animateTag(tag);
		}

		document.fonts.ready.then(() => {
			gsap.set(title, { opacity: 1 });

			SplitText.create(title, {
				type: "words, lines",
				linesClass: "line",
				mask: "lines",
				autoSplit: true,
				onSplit: (self) => {
					return gsap.from(self.words, {
						yPercent: 120,
						autoAlpha: 0,
						filter: "blur(10px)",
						duration: 0.35,
						stagger: 0.015,
						delay: REVEAL_DELAY,
						ease: "expo.out",
						scrollTrigger: { trigger: title, start: "top 85%" },
					});
				},
			});
		});
	}
}
